// SignalTower — the Contact-scene landmark. A tall tower: cylinder
// 0.08r x 1h, antenna 0.02r x 0.4h on top, with a small pulsing point
// at the tip. Strong point light at the top (intensity 1.5, color
// mint, distance 3) — it's a beacon.

import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import type { Group, Mesh } from 'three';

const COLOR_TOWER = '#b8a572'; // gold
const COLOR_ANTENNA = '#7fb89f'; // mint
const COLOR_LIGHT = '#7fb89f'; // mint

export interface SignalTowerProps {
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number;
  isActive?: boolean;
  isHovered?: boolean;
  groupRef?: React.MutableRefObject<Group | null>;
}

export function SignalTower({
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = 1,
  isActive = false,
  isHovered = false,
  groupRef,
}: SignalTowerProps) {
  const tipRef = useRef<Mesh | null>(null);
  const lightRef = useRef<THREE.PointLight | null>(null);
  const t0 = useRef(performance.now() / 1000);

  const combinedScale = useMemo(() => {
    const base = scale;
    const hover = isHovered ? 1.08 : 1;
    const active = isActive ? 1.12 : 1;
    return base * hover * active;
  }, [scale, isHovered, isActive]);

  useFrame(() => {
    const t = performance.now() / 1000 - t0.current;
    // Pulse on a 1.6s cycle.
    const k = (t * 2 * Math.PI) / 1.6;
    const pulse = 0.5 + 0.5 * Math.sin(k);
    if (tipRef.current) {
      const s = 1 + pulse * 0.5;
      tipRef.current.scale.setScalar(s);
    }
    if (lightRef.current) {
      lightRef.current.intensity = 1.0 + pulse * 1.0; // 1.0..2.0
    }
  });

  return (
    <group
      ref={groupRef as React.MutableRefObject<Group>}
      name="landmark-signal-tower"
      position={position}
      rotation={rotation}
      scale={combinedScale}
    >
      {/* Base — small disc to anchor the tower. */}
      <mesh name="tower-base" position={[0, 0.05, 0]}>
        <cylinderGeometry args={[0.18, 0.22, 0.1, 6]} />
        <meshStandardMaterial color={COLOR_TOWER} roughness={0.85} flatShading />
      </mesh>

      {/* Tower — 0.08r x 1h cylinder. */}
      <mesh
        name="tower-body"
        position={[0, 0.6, 0]}
        userData={{ landmarkType: 'signal_tower' }}
      >
        <cylinderGeometry args={[0.08, 0.1, 1, 6]} />
        <meshStandardMaterial
          color={COLOR_TOWER}
          emissive={COLOR_TOWER}
          emissiveIntensity={0.2}
          roughness={0.7}
          flatShading
        />
      </mesh>

      {/* Antenna — 0.02r x 0.4h cylinder. */}
      <mesh
        name="tower-antenna"
        position={[0, 1.3, 0]}
        userData={{ landmarkType: 'signal_tower' }}
      >
        <cylinderGeometry args={[0.02, 0.025, 0.4, 5]} />
        <meshStandardMaterial
          color={COLOR_ANTENNA}
          emissive={COLOR_ANTENNA}
          emissiveIntensity={0.4}
          roughness={0.5}
          flatShading
        />
      </mesh>

      {/* Antenna tip — small pulsing ball. */}
      <mesh
        ref={tipRef}
        name="tower-tip"
        position={[0, 1.55, 0]}
        userData={{ landmarkType: 'signal_tower' }}
      >
        <sphereGeometry args={[0.05, 10, 8]} />
        <meshStandardMaterial
          color={COLOR_LIGHT}
          emissive={COLOR_LIGHT}
          emissiveIntensity={1.0}
        />
      </mesh>

      {/* Strong point light at the top — this is the beacon. */}
      <pointLight
        ref={lightRef}
        color={COLOR_LIGHT}
        intensity={1.5}
        distance={3}
        position={[0, 1.55, 0]}
      />

      {/* Secondary lower light for fill. */}
      <pointLight
        color={COLOR_TOWER}
        intensity={0.4}
        distance={1.5}
        position={[0, 0.6, 0]}
      />
    </group>
  );
}

export default SignalTower;
